// src/pages/Coach/LastTaught.jsx
// STATUS: active | UPDATED: 2026-09-26
// LOG:
//   2026-09-26  New. The one part of the generated opener worth keeping,
//               rebuilt as a query so it costs a hundred milliseconds instead
//               of a model round trip.
//
// ONE THING HE TAUGHT US, QUOTED BACK.
//
// There used to be a "welcome back" composed by the model before Jazz had
// typed anything. It was slow, it opened with the backlog, and both of his
// complaints on 2026-09-26 were about it. It is gone.
//
// But one instruction inside it was right, and deleting the whole thing would
// have taken it too: show him ONE thing he taught us, in HIS words, because
// that is the only thing on the screen that proves the three hours he spent
// dictating went somewhere. A count does not do it. A thank you does not do
// it. His own sentence, sitting there, does.
//
// WHY IT IS A QUERY AND NOT A COMPLETION.
//
// Choosing which line to quote is the only part a model was doing, and it was
// doing it badly, because it saw a list and picked the first one. A recency
// ordering picks better and returns in one round trip. The model was being
// paid to read something back that was already written down, which is the same
// mistake as asking him to approve a quotation of himself.
//
// IT NEVER BLOCKS AND IT NEVER APOLOGISES. It renders nothing until the row
// arrives and nothing at all if the read fails, because a greeting that says
// "could not load your contributions" is worse than a greeting that is simply
// one line shorter. Nobody but us knows it was meant to be there.
//
// AUTHOR ONLY. Quoting a salesperson's own words back at them is a coaching
// move with a completely different meaning, and it is not this one.

import { useEffect, useState } from 'react'
import { Box, Text, Skeleton } from '@chakra-ui/react'
import { supabase } from '../../lib/supabase'

// Long enough to be a real line, short enough to read in one glance on a
// phone. A three hundred character word track quoted in a greeting is a wall,
// and the point of this is that it lands in a second.
const MAX = 220

// The kinds that read well as a quotation. A `reason` or a `definition` is him
// explaining something to us, which is valuable and does not sound like
// himself when it is pulled out of its conversation and put in quote marks.
const QUOTABLE = ['word_track', 'acceptable_variant', 'correction']

export default function LastTaught({ enabled }) {
  const [row, setRow] = useState(undefined) // undefined = loading, null = show nothing

  useEffect(() => {
    if (!enabled) {
      setRow(null)
      return undefined
    }
    let alive = true
    supabase
      .from('method_contributions')
      .select('id, kind, content, step_number, updated_at')
      .eq('status', 'applied')
      .in('kind', QUOTABLE)
      .order('updated_at', { ascending: false })
      .limit(12)
      .then(({ data, error }) => {
        if (!alive) return
        if (error || !data?.length) {
          setRow(null)
          return
        }
        // The most recent one that fits. Ordering by recency and THEN filtering
        // by length beats asking the database for short ones, because the
        // newest thing he said is the one he remembers saying.
        const fits = data.find((r) => r.content && r.content.trim().length <= MAX)
        setRow(fits || null)
      })
    return () => {
      alive = false
    }
  }, [enabled])

  if (!enabled) return null

  // A single line of skeleton rather than a spinner. It reserves the space so
  // the greeting does not jump when the row lands, and it is quiet enough that
  // nobody waits for it.
  if (row === undefined) {
    return <Skeleton height="18px" maxW="380px" mb={6} borderRadius="sm" speed={1.2} />
  }
  if (!row) return null

  return (
    <Box
      mb={6}
      pl={4}
      borderLeft="2px solid"
      borderColor="accent.500"
      maxW="measure"
    >
      <Text fontSize="body-sm" color="inkDim" mb={1}>
        {row.step_number ? `The last thing you taught me, Step ${row.step_number}` : 'The last thing you taught me'}
      </Text>
      <Text fontSize={{ base: 'body-sm', md: 'body' }} color="inkMuted" lineHeight={1.6} fontStyle="italic">
        {/* Real quotation marks, and his text untouched inside them. He
            dictates, so his lines carry dropped punctuation and the odd word
            the phone heard wrong. Tidying them would be editing him. */}
        &ldquo;{row.content.trim()}&rdquo;
      </Text>
    </Box>
  )
}
